import {useState, useEffect, useMemo, useRef, useCallback} from 'react'
import {useLocation} from 'react-router-dom'
import {Grid} from 'antd'

import {useAuthStore} from '@/stores'
import {useAuthNotificationStore} from '@/stores/notification'
import {useStorageNotification} from '@/hooks'
import {computeOpenKeys, computeSelectedKey} from '@/services/menu'

const {useBreakpoint} = Grid

/**
 * Custom hook that handles common layout functionality
 * @param store The layout store (MainLayoutStore or OnboardingLayoutStore)
 * @param menuItems Menu items to compute selected and open keys
 * @returns Common layout state and functions
 */
export const useLayout = (store, menuItems) => {
    const authStore = useAuthStore()
    const location = useLocation()

    // Responsive behavior
    const screens = useBreakpoint()
    const isMobile = screens.hasOwnProperty('md') && !screens.md

    // Route and menu state
    const currentRoute = window.location.pathname

    // auto-open based on route (when store.openKeys === null)
    const autoOpenKeys = useMemo(
        () => computeOpenKeys(currentRoute, menuItems),
        [currentRoute, menuItems]
    )

    // final openKeys passed to Menu
    const openKeys = store.openKeys === null ? autoOpenKeys : store.openKeys

    // store last "valid" openKeys when sider is expanded
    const lastExpandedOpenKeysRef = useRef<string[]>(openKeys)

    useEffect(() => {
        if (!store.isCollapsed) {
            lastExpandedOpenKeysRef.current = openKeys
        }
    }, [store.isCollapsed, openKeys])

    // restore openKeys after expanding if they were reset due to collapse
    useEffect(() => {
        if (!store.isCollapsed && store.openKeys !== null && store.openKeys.length === 0) {
            // if menu was collapsed due to sider collapse — restore previous state
            store.setOpenKeys(lastExpandedOpenKeysRef.current)
        }
    }, [store.isCollapsed, store])

    // ignore onOpenChange while collapsed to prevent antd from overwriting openKeys with []
    const onOpenChange = useCallback(
        (keys: string[]) => {
            if (store.isCollapsed) return
            store.setOpenKeys(keys)
        },
        [store]
    )

    // selectedKey
    const [selectedKey, setSelectedKey] = useState(computeSelectedKey(currentRoute, menuItems))

    // Notification state
    const {sections: sectionsNotifications} = useAuthNotificationStore()
    const currentUserEmail = authStore.email

    // Current section notification
    const currentSectionNotification = Object.values(sectionsNotifications).find(
        (section) => section.route === currentRoute
    )
    const {isClosed: isSectionNotificationClosed, onCloseNotification: onCloseSectionNotification} =
        useStorageNotification(currentSectionNotification, 'auth', currentUserEmail)

    // Popup notification
    const popupNotification = sectionsNotifications['auth']
    const {isClosed: isPopupNotificationClosed, onCloseNotification: onClosePopupNotification} =
        useStorageNotification(popupNotification, 'auth', currentUserEmail)

    // Session management
    const onCancelSession = () => {
        console.log('CANCELLING SESSION')
        authStore.logout(false, {trigger: 'session-expiry'})
    }

    // Effects
    useEffect(() => {
        // Hide menu in mobile device
        isMobile && store.setCollapsed(isMobile)
    }, [isMobile, store])

    useEffect(() => {
        setSelectedKey(computeSelectedKey(location.pathname, menuItems))
    }, [location.pathname, menuItems])

    return {
        // State
        isMobile,
        isCollapsed: store.isCollapsed,
        currentRoute,
        selectedKey,
        openKeys,
        merchant: store.merchant,

        // Notifications
        currentSectionNotification,
        isSectionNotificationClosed,
        onCloseSectionNotification,
        popupNotification,
        isPopupNotificationClosed,
        onClosePopupNotification,

        // Functions
        onOpenChange,
        setCollapsed: store.setCollapsed,
        onCancelSession
    }
}
